// react_app/src/components/admin/translations/ImportPreviewDialog.tsx

import { useState } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { useTranslation } from '../../../i18n/useTranslation';
import { translationApi, type LanguageTextDto } from '../../../api/translationService';
import { invalidateCachedTranslations } from '../../../i18n/translationCache';

interface ImportChange {
  key: string;
  newValue: string;
  oldValue: string;
  type: 'new' | 'updated';
}

interface ImportPreviewDialogProps {
  cultureName: string;
  csvContent: string;
  existingTexts: LanguageTextDto[];
  onClose: () => void;
  onComplete: () => void;
}

function parseCSV(csv: string): Array<{ key: string; translation: string }> {
  const lines = csv.split('\n').filter((l) => l.trim() !== '');
  if (lines.length < 2) return [];

  const results: Array<{ key: string; translation: string }> = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',').map((c) => c.replace(/^"|"$/g, '').trim());
    if (cols.length >= 3 && cols[0]) {
      results.push({ key: cols[0], translation: cols[2] || '' });
    }
  }
  return results;
}

export function ImportPreviewDialog({
  cultureName,
  csvContent,
  existingTexts,
  onClose,
  onComplete,
}: ImportPreviewDialogProps) {
  const { t } = useTranslation();
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);

  const existingMap = new Map(existingTexts.map((tx) => [tx.name, tx.value || '']));
  const parsed = parseCSV(csvContent);

  const changes: ImportChange[] = [];
  let unchanged = 0;
  let unrecognized = 0;

  for (const row of parsed) {
    if (!row.translation) continue;
    if (!existingMap.has(row.key)) {
      unrecognized++;
      continue;
    }
    const existing = existingMap.get(row.key)!;
    if (existing === row.translation) {
      unchanged++;
    } else {
      changes.push({
        key: row.key,
        newValue: row.translation,
        oldValue: existing,
        type: existing === '' ? 'new' : 'updated',
      });
    }
  }

  const newCount = changes.filter((c) => c.type === 'new').length;
  const updatedCount = changes.filter((c) => c.type === 'updated').length;

  const handleApply = async () => {
    setImporting(true);
    setProgress(0);
    for (let i = 0; i < changes.length; i++) {
      try {
        await translationApi.updateLanguageText(cultureName, changes[i].key, changes[i].newValue);
      } catch {
        // Continue on error
      }
      setProgress(i + 1);
    }
    await invalidateCachedTranslations(cultureName);
    onComplete();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2100] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold">{t('App.Admin.Translations.Import.Title')}</h2>
          <button onClick={onClose} disabled={importing} className="p-2 hover:bg-neutral-100 rounded-md min-h-[44px] min-w-[44px]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-neutral-600">{t('App.Admin.Translations.Import.New')}</span>
            <span className="font-medium text-success-600">{newCount}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-neutral-600">{t('App.Admin.Translations.Import.Updated')}</span>
            <span className="font-medium text-primary-600">{updatedCount}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-neutral-600">{t('App.Admin.Translations.Import.Unchanged')}</span>
            <span className="text-neutral-400">{unchanged}</span>
          </div>
          {unrecognized > 0 && (
            <div className="flex justify-between text-sm">
              <span className="flex items-center gap-1 text-warning-600">
                <AlertTriangle className="w-4 h-4" />
                {t('App.Admin.Translations.Import.Unrecognized')}
              </span>
              <span className="text-warning-600">{unrecognized}</span>
            </div>
          )}

          {importing && (
            <div className="mt-4">
              <div className="flex items-center gap-2 text-sm text-neutral-500 mb-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('App.Admin.Translations.Import.Applying', { current: progress, total: changes.length })}
              </div>
              <div className="w-full bg-neutral-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all"
                  style={{ width: `${(progress / changes.length) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-200">
          <button
            onClick={onClose}
            disabled={importing}
            className="px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-100 rounded-md min-h-[44px]"
          >
            {t('App.Common.Cancel')}
          </button>
          <button
            onClick={handleApply}
            disabled={importing || changes.length === 0}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 min-h-[44px]"
          >
            {changes.length === 0
              ? t('App.Admin.Translations.Import.NoChanges')
              : t('App.Admin.Translations.Import.Apply', { count: changes.length })}
          </button>
        </div>
      </div>
    </div>
  );
}
